import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/api';
import { getAdminAuthHeader } from '../utils/adminAuth';

function GameAgent() {
  const navigate = useNavigate();
  const [discovering, setDiscovering] = useState(false);
  const [games, setGames] = useState([]);
  const [error, setError] = useState(null);
  const [pageStart, setPageStart] = useState(1);
  const [pageEnd, setPageEnd] = useState(2);
  const [loadingImages, setLoadingImages] = useState(new Set());

  const handleDiscover = async () => {
    try {
      setDiscovering(true);
      setError(null);
      setGames([]);

      const authHeader = getAdminAuthHeader();
      const pagesToScrape = pageEnd - pageStart + 1;
      const response = await fetch(`${API_URL}/admin/agent/discover`, {
        method: 'POST',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pagesToScrape: pagesToScrape,
          startPage: pageStart,
          sortBy: 'popular' // Default to most popular
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to discover games';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } else {
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const discoveredGames = data.games || [];
      setGames(discoveredGames);
      
      // Start loading images in background for each game
      if (discoveredGames.length > 0) {
        loadImagesInBackground(discoveredGames);
      }
    } catch (err) {
      console.error('Error discovering games:', err);
      setError(err.message || 'Failed to discover games');
    } finally {
      setDiscovering(false);
    }
  };

  // Load images in background, one by one
  const loadImagesInBackground = async (gamesList) => {
    for (const game of gamesList) {
      // Skip if already has images or is already loading
      if (game.coverImage && game.screenshots?.length > 0) continue;
      if (loadingImages.has(game.f95Url)) continue;
      
      setLoadingImages(prev => new Set(prev).add(game.f95Url));
      
      try {
        const authHeader = getAdminAuthHeader();
        const response = await fetch(
          `${API_URL}/admin/agent/scrape-thread?f95Url=${encodeURIComponent(game.f95Url)}`,
          {
            headers: {
              'Authorization': authHeader,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            // Update the game with new image data
            setGames(prevGames => 
              prevGames.map(g => 
                g.f95Url === game.f95Url
                  ? {
                      ...g,
                      coverImage: data.data.coverImage || g.coverImage,
                      screenshots: data.data.screenshots || [],
                      description: data.data.description || g.description,
                      itchUrl: data.data.itchUrl || g.itchUrl
                    }
                  : g
              )
            );
          }
        }
      } catch (err) {
        console.error(`Error loading images for ${game.title}:`, err);
      } finally {
        setLoadingImages(prev => {
          const next = new Set(prev);
          next.delete(game.f95Url);
          return next;
        });
      }
      
      // Small delay between requests to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  };

  const handleConfirm = (game) => {
    // Redirect to scraper page with pre-filled links
    const params = new URLSearchParams({
      f95Url: game.f95Url,
      source: 'f95' // Use F95Zone as primary source
    });
    
    // Add itch.io URL if available
    if (game.itchUrl) {
      params.set('itchUrl', game.itchUrl);
    }
    
    // Navigate to admin dashboard with scraper tab and pre-filled data
    navigate(`/admin/dashboard?tab=scraper&${params.toString()}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Game Agent</h2>
        <div className="text-white/40 text-xs uppercase tracking-wider">
          Automatically discover games from F95Zone forums
        </div>
      </div>

      {/* Discovery Controls */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-6">
        <div className="space-y-4">
          <div>
            <label className="block text-white/60 text-sm font-medium mb-2">
              Page Range
            </label>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-white/40 text-xs mb-1">Start Page</label>
                <input
                  type="number"
                  value={pageStart}
                  onChange={(e) => setPageStart(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-accent-blue/50 transition-colors"
                />
              </div>
              <div className="flex-1">
                <label className="block text-white/40 text-xs mb-1">End Page</label>
                <input
                  type="number"
                  value={pageEnd}
                  onChange={(e) => setPageEnd(Math.max(pageStart, parseInt(e.target.value) || 2))}
                  min={pageStart}
                  className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-accent-blue/50 transition-colors"
                />
              </div>
            </div>
            <div className="text-white/40 text-xs mt-2">
              Scrapes pages {pageStart} through {pageEnd} from F95Zone games forum
            </div>
          </div>

          <button
            onClick={handleDiscover}
            disabled={discovering}
            className="w-full px-6 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {discovering ? 'Discovering Games...' : 'Start Discovery'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Discovered Games */}
      {games.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              Discovered Games ({games.length})
            </h3>
            <div className="text-white/40 text-sm">
              Click "Add Game" to review and add
            </div>
          </div>

          <div className="space-y-4">
            {games.map((game, index) => (
              <GameCard
                key={game.f95Url || index}
                game={game}
                onConfirm={() => handleConfirm(game)}
                isLoadingImages={loadingImages.has(game.f95Url)}
              />
            ))}
          </div>
        </div>
      )}

      {!discovering && games.length === 0 && !error && (
        <div className="bg-dark-card border border-dark-border rounded-lg p-8 text-center">
          <div className="text-white/60 text-sm">
            Click "Start Discovery" to find games automatically
          </div>
        </div>
      )}
    </div>
  );
}

function GameCard({ game, onConfirm, isLoadingImages = false }) {
  // Combine cover image and screenshots into one ordered array
  const allImages = React.useMemo(() => {
    const images = [];
    if (game.coverImage) {
      images.push(game.coverImage);
    }
    if (game.screenshots && game.screenshots.length > 0) {
      images.push(...game.screenshots);
    }
    return images;
  }, [game.coverImage, game.screenshots]);

  return (
    <div className="bg-dark-card border border-dark-border rounded-lg p-6 hover:border-accent-blue/50 transition-colors w-full">

      {/* Game Info */}
      <div className="space-y-4">
        <div>
          <h4 className="text-white font-semibold text-xl mb-2">{game.title}</h4>
          {game.description && (
            <div className="text-white/80 text-sm mb-4">
              {game.description}
            </div>
          )}
        </div>

        {/* All Images - Horizontal Scroll */}
        {allImages.length > 0 ? (
          <div className="mb-4">
            <div className="text-white/60 text-sm mb-2">
              Images ({allImages.length})
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-dark-border scrollbar-track-dark-bg">
              {allImages.map((imageUrl, idx) => (
                <ImageThumbnail 
                  key={idx} 
                  src={imageUrl} 
                  alt={`${game.title} - Image ${idx + 1}`}
                  index={idx}
                />
              ))}
            </div>
          </div>
        ) : isLoadingImages ? (
          <div className="mb-4">
            <div className="text-white/60 text-sm mb-2">Loading images...</div>
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-accent-blue/30 border-t-accent-blue rounded-full animate-spin"></div>
            </div>
          </div>
        ) : null}


        {/* Stats */}
        {(game.replies !== undefined || game.views !== undefined) && (
          <div className="space-y-1 pt-2 border-t border-dark-border">
            {game.replies !== undefined && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-white/60">F95Zone Replies:</span>
                <span className="text-white">{game.replies.toLocaleString()}</span>
              </div>
            )}
            {game.views !== undefined && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-white/60">F95Zone Views:</span>
                <span className="text-white">{game.views.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {/* Links */}
        <div className="space-y-2 pt-2 border-t border-dark-border">
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/60">F95Zone:</span>
            <a
              href={game.f95Url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-blue hover:underline truncate ml-2"
            >
              View Thread
            </a>
          </div>
          {game.itchUrl && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/60">Itch.io:</span>
              <a
                href={game.itchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-blue hover:underline truncate ml-2"
              >
                View Page
              </a>
            </div>
          )}
        </div>

        {/* Confirm Button */}
        <button
          onClick={onConfirm}
          className="w-full px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg text-green-400 font-medium hover:bg-green-500/30 hover:border-green-500/40 transition-all duration-200"
        >
          Add Game
        </button>
      </div>
    </div>
  );
}

// Optimized image thumbnail component for horizontal scrolling
function ImageThumbnail({ src, alt, index }) {
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState(false);

  return (
    <div className="relative flex-shrink-0 w-64 h-40 bg-dark-border rounded-lg border border-dark-border overflow-hidden group">
      {!loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-dark-surface">
          <div className="w-6 h-6 border-2 border-accent-blue/30 border-t-accent-blue rounded-full animate-spin"></div>
        </div>
      )}
      {error ? (
        <div className="w-full h-full flex items-center justify-center bg-dark-surface">
          <div className="text-white/40 text-xs">Failed to load</div>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 cursor-pointer hover:scale-105 transition-transform ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => {
            setError(true);
            setLoaded(false);
          }}
          onClick={() => window.open(src, '_blank')}
          title={`${alt} - Click to view full size`}
        />
      )}
      {loaded && !error && (
        <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
          {index + 1}
        </div>
      )}
    </div>
  );
}

export default GameAgent;
